import { useEffect, useState, useRef } from 'react';
import { api, ConversationItem, MessageItem } from '../lib/api';
import { useAuth } from '../store/authStore';

export default function Messages() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<ConversationItem[]>([]);
  const [selectedConv, setSelectedConv] = useState<ConversationItem | null>(null);
  const [messages, setMessages] = useState<MessageItem[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await api.conversations.list();
        setConversations(data.conversations);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load conversations');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user?.id]);

  useEffect(() => {
    if (!selectedConv) return;
    const load = async () => {
      try {
        const data = await api.conversations.messages(selectedConv.id);
        setMessages(data.messages);
      } catch {
        // ignore
      }
    };
    load();
  }, [selectedConv]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConv || sending) return;
    setSending(true);
    try {
      const msg = await api.conversations.sendMessage(selectedConv.id, newMessage.trim());
      setMessages((prev) => [...prev, msg]);
      setNewMessage('');
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div
        className="messages-page"
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <div className="loading-state">Loading messages...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="messages-page"
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  const otherParticipant = (conv: ConversationItem) => {
    if (!user) return null;
    return conv.participants.find((p) => p.user_id !== user.id) || conv.participants[0];
  };

  return (
    <div className="messages-page">
      <h2 className="messages-title">Messages</h2>
      <div className="messages-layout">
        <aside className="messages-sidebar">
          {conversations.length === 0 ? (
            <div className="empty-state">No conversations yet</div>
          ) : (
            <ul className="conversation-list">
              {conversations.map((conv) => {
                const other = otherParticipant(conv);
                return (
                  <li
                    key={conv.id}
                    className={`conversation-item ${selectedConv?.id === conv.id ? 'active' : ''} ${conv.unread_count > 0 ? 'unread' : ''}`}
                    onClick={() => setSelectedConv(conv)}
                  >
                    <div className="conv-avatar">
                      {other ? (
                        <img
                          src={
                            other.avatar_url ||
                            `https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&s=32`
                          }
                          alt=""
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              `https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&s=32`;
                          }}
                        />
                      ) : (
                        <div className="conv-avatar-placeholder">?</div>
                      )}
                    </div>
                    <div className="conv-info">
                      <div className="conv-name">{other?.name || 'Unknown'}</div>
                      <div className="conv-preview">{conv.last_message || 'No messages yet'}</div>
                    </div>
                    {conv.unread_count > 0 && (
                      <span className="unread-badge">{conv.unread_count}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        <section className="messages-main">
          {selectedConv ? (
            <>
              <div className="messages-header">
                <h3>{selectedConv.subject}</h3>
                {selectedConv.product_id && (
                  <span className="conv-product-ref">About product #{selectedConv.product_id}</span>
                )}
              </div>

              <div className="messages-thread">
                {messages.length === 0 ? (
                  <div className="empty-state">No messages yet. Start the conversation!</div>
                ) : (
                  messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`message-bubble ${msg.sender_id === user?.id ? 'own' : 'other'}`}
                    >
                      <div className="message-content">{msg.content}</div>
                      <div className="message-time">
                        {new Date(msg.created_at).toLocaleString()}
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              <form className="message-input-form" onSubmit={handleSend}>
                <input
                  type="text"
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  disabled={sending}
                />
                <button type="submit" disabled={!newMessage.trim() || sending}>
                  Send
                </button>
              </form>
            </>
          ) : (
            <div className="empty-state">Select a conversation to view messages</div>
          )}
        </section>
      </div>
    </div>
  );
}
